import * as React from "react";

import { getErrorMonitoringProvider } from "@stadium/services/error-reporting/provider";
import { ErrorBoundary as SentryErrorBoundary } from "@stadium/services/sentry-nextjs/error-boundary";
import { captureReactException } from "@stadium/services/error-reporting";

export type {
  FallbackRender,
  ErrorBoundaryProps,
} from "@stadium/services/sentry-nextjs/error-boundary";

const INITIAL_STATE = {
  componentStack: null,
  error: null,
  eventId: null,
};

type DatadogErrorBoundaryState =
  | typeof INITIAL_STATE
  | {
      componentStack: string;
      error: unknown;
      eventId: string | null;
    };

class DatadogErrorBoundary extends React.Component<
  import("@stadium/services/sentry-nextjs/error-boundary").ErrorBoundaryProps,
  DatadogErrorBoundaryState
> {
  state: DatadogErrorBoundaryState = INITIAL_STATE;

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo): void {
    const { componentStack } = errorInfo;
    const { onError } = this.props;
    const handled = this.props.handled != null ? this.props.handled : !!this.props.fallback;
    const eventId = captureReactException(error, errorInfo, { mechanism: { handled } }) || "";

    if (onError) {
      onError(error, componentStack, eventId || "");
    }

    this.setState({ error, componentStack, eventId });
  }

  resetErrorBoundary(): void {
    const { onReset } = this.props;
    const { error, componentStack, eventId } = this.state;
    if (onReset && error && componentStack) {
      onReset(error, componentStack, eventId || "");
    }
    this.setState(INITIAL_STATE);
  }

  render(): React.ReactNode {
    const { fallback, children } = this.props;
    const state = this.state;

    if (state.componentStack === null) {
      return typeof children === "function" ? children() : children;
    }

    const element =
      typeof fallback === "function"
        ? React.createElement(fallback, {
            error: state.error,
            componentStack: state.componentStack,
            resetError: () => this.resetErrorBoundary(),
            eventId: state.eventId || "",
          })
        : fallback;

    if (React.isValidElement(element)) {
      return element;
    }

    return null;
  }
}

export const ErrorBoundary = (
  props: import("@stadium/services/sentry-nextjs/error-boundary").ErrorBoundaryProps,
) => {
  const provider = getErrorMonitoringProvider();
  if (provider === "sentry") {
    return React.createElement(SentryErrorBoundary, props);
  }
  if (provider === "datadog") {
    return React.createElement(DatadogErrorBoundary, props);
  }
  const { fallback, children } = props;
  return typeof children === "function" ? children() : children;
};
